/**
 * PermissionsMatrix — UI dla granular action permissions sesji autopilota.
 *
 * Pozwala superadminowi na per-action toggle: auto / require_approval / disabled.
 * Defaulty pochodzą z session.mode; explicit override zapisuje się jako
 * autopilotSessions.actionPermissions jsonb.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, ShieldX, Loader2 } from "lucide-react";
import {
  usePermissionMatrix,
  useUpdatePermissions,
  type AutopilotAction,
  type PermissionMode,
} from "@/hooks/useActionPermissions";

const ACTION_LABELS: Record<AutopilotAction, string> = {
  scaling_up: "Skalowanie w górę (zwiększanie budżetu)",
  scaling_down: "Skalowanie w dół (cięcie budżetu)",
  killing_ads: "Wyłączanie reklam (kill underperformers)",
  creating_new_ads: "Tworzenie nowych reklam (iteracje)",
  ai_generation: "Generacja AI (HeyGen / fal / Runway)",
  publishing_to_meta: "Publikacja na Meta Ads",
  budget_changes: "Zmiany budżetu (CBO/ABO)",
  audience_changes: "Zmiany audience (broad / lookalike)",
  creative_rotation: "Rotacja kreacji (winners ↔ on_deck)",
  duplicate_winner: "Duplikacja winners (Theriot 1×AOV scale)",
};

const MODE_BADGE: Record<
  PermissionMode,
  { color: string; icon: typeof ShieldCheck; label: string }
> = {
  auto: {
    color: "bg-emerald-500/20 text-emerald-600 border-emerald-500/40",
    icon: ShieldCheck,
    label: "Auto",
  },
  require_approval: {
    color: "bg-amber-500/20 text-amber-600 border-amber-500/40",
    icon: ShieldAlert,
    label: "Wymaga zgody",
  },
  disabled: {
    color: "bg-red-500/20 text-red-600 border-red-500/40",
    icon: ShieldX,
    label: "Zablokowane",
  },
};

export function PermissionsMatrix({ sessionId }: { sessionId: string }) {
  const { data, isLoading, isError } = usePermissionMatrix(sessionId);
  const updateMutation = useUpdatePermissions(sessionId);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-red-600">
          Nie udało się załadować macierzy uprawnień.
        </CardContent>
      </Card>
    );
  }

  const handleChange = (action: AutopilotAction, mode: PermissionMode) => {
    // Wysyłamy tylko zmienioną akcję — backend mergeuje z istniejącym jsonb.
    const merged: Partial<Record<AutopilotAction, PermissionMode>> = {};
    for (const [k, v] of Object.entries(data.permissions)) {
      merged[k as AutopilotAction] = v.mode;
    }
    merged[action] = mode;
    updateMutation.mutate(merged);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4" /> Uprawnienia akcji autopilota
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Tryb sesji: <Badge variant="secondary">{data.mode}</Badge>. Per-akcja override zastępuje
          default tego trybu.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {(Object.keys(ACTION_LABELS) as AutopilotAction[]).map((action) => {
          const decision = data.permissions[action];
          if (!decision) return null;
          const meta = MODE_BADGE[decision.mode];
          const Icon = meta.icon;
          return (
            <div
              key={action}
              className="flex items-center justify-between gap-3 rounded border px-3 py-2 text-sm"
            >
              <div className="flex-1">
                <div className="font-medium">{ACTION_LABELS[action]}</div>
                <div className="text-xs text-muted-foreground">{decision.reason}</div>
              </div>
              <Badge variant="outline" className={meta.color}>
                <Icon className="mr-1 h-3 w-3" /> {meta.label}
              </Badge>
              <Select
                value={decision.mode}
                onValueChange={(v) => handleChange(action, v as PermissionMode)}
                disabled={updateMutation.isPending}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (od razu)</SelectItem>
                  <SelectItem value="require_approval">Wymaga zgody</SelectItem>
                  <SelectItem value="disabled">Zablokowane</SelectItem>
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
